import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { CheckIcon, LoaderIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { DS } from '../ds';
import { AlinovaConstellation, SmileDivider } from '../Visuals';
import { Btn, Label, P } from '../Primitives';
const EMAILJS_SERVICE = 'service_g4qrd3j';
const EMAILJS_TEMPLATE = 'template_vuw3rok';
const EMAILJS_KEY = 'a3z7AOkt8EwKXU8c-';
type Status = 'idle' | 'loading' | 'success' | 'error';
interface FormState {
  name: string;
  email: string;
  phone: string;
  modality: string;
}
const modalities = [{
  value: 'fabricacion',
  label: 'Tú planeas, nosotros fabricamos'
}, {
  value: 'integral',
  label: 'Solución integral'
}, {
  value: 'flexible',
  label: 'Pedido flexible'
}, {
  value: 'consulta',
  label: 'Aún tengo dudas'
}];
const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  borderRadius: '12px',
  border: `1px solid ${DS.color.border}`,
  background: DS.color.white,
  color: DS.color.ink,
  fontFamily: DS.font.body,
  fontSize: '14px',
  outline: 'none',
  transition: `all 0.22s ${DS.ease.smooth}`,
  boxSizing: 'border-box'
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: DS.font.body,
  fontSize: '12px',
  fontWeight: 600,
  color: DS.color.muted,
  marginBottom: '7px'
};
export const ContactSection = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    modality: 'integral'
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | undefined>(undefined);
  const set = (key: keyof FormState, value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    if (errors[key]) setErrors(current => ({
      ...current,
      [key]: undefined
    }));
    if (status === 'error') setStatus('idle');
  };
  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Ingresa tu nombre';
    if (!form.email.trim()) next.email = 'Ingresa tu correo';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Correo no válido';
    if (!form.phone.trim()) next.phone = 'Ingresa un teléfono de contacto';
    setErrors(next);
    const consentOk = consent;
    setConsentError(consentOk ? undefined : 'Debes autorizar el tratamiento de tus datos para continuar');
    return Object.keys(next).length === 0 && consentOk;
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (status === 'loading' || !validate()) return;
    const modalityLabel = modalities.find(m => m.value === form.modality)?.label ?? form.modality;
    setStatus('loading');
    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        modalidad: modalityLabel
      }, EMAILJS_KEY);
      setStatus('success');
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    }
  };
  const focusStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.aqua;
    event.currentTarget.style.background = DS.color.mint;
    event.currentTarget.style.boxShadow = '0 0 0 3px rgba(75,199,210,0.12)';
  };
  const blurStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.border;
    event.currentTarget.style.background = DS.color.white;
    event.currentTarget.style.boxShadow = 'none';
  };
  const resetForm = () => {
    setStatus('idle');
    setErrors({});
    setForm({
      name: '',
      email: '',
      phone: '',
      modality: 'integral'
    });
    setConsent(false);
    setConsentError(undefined);
  };
  return <section id="contacto" style={{
    background: DS.color.ivory,
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-326" data-mp="iw3poxhfpr46">
      <SmileDivider fromColor={DS.color.linen} toColor={DS.color.ivory} data-id="element-327" data-mp="duwmlbq8lg3u" />

      {/* Grid CAD sutil */}
      <div aria-hidden="true" style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'linear-gradient(rgba(26,95,115,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(26,95,115,0.025) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      pointerEvents: 'none',
      zIndex: 0
    }} data-id="element-328" data-mp="3yl76cvcoa56" />

      <div style={{
      padding: '40px 24px 96px',
      position: 'relative',
      zIndex: 1
    }} data-id="element-329" data-mp="3ma7vxywdasy7">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-330" data-mp="3hfamwrz38jlb">
          <div className="col-2" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '64px',
          alignItems: 'center'
        }} data-id="element-331" data-mp="3cdbjxj79foow">
            {/* Columna izquierda — texto */}
            <div style={{
            position: 'relative'
          }} data-id="element-332" data-mp="1rvsxx5v0g4fl">
              <AlinovaConstellation width={320} height={320} opacity={0.05} style={{
              position: 'absolute',
              top: '-70px',
              left: '-70px',
              pointerEvents: 'none'
            }} data-id="element-333" data-mp="1ruj08hs2ky9h" />
              <div style={{
              position: 'relative',
              zIndex: 1
            }} data-id="element-334" data-mp="195uj86wpjd2z">
                <Label data-id="element-335" data-mp="196463lmgh44g">Empecemos</Label>
                <h2 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                background: `linear-gradient(135deg, ${DS.color.aqua}, ${DS.color.teal})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.12,
                letterSpacing: '-0.025em',
                marginBottom: '20px'
              }} data-id="element-336" data-mp="1e57cb9e69ge0">
                  Tu primer caso empieza aquí.
                </h2>
                <P size="lg" maxWidth="420px" data-id="element-337" data-mp="s6kv62bhmg2x">
                  Un asesor Alinova te guía en cada paso. Respuesta en menos de 24h.
                </P>

                <a href={`tel:+${DS.phone}`} style={{
                marginTop: '22px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: DS.color.teal,
                textDecoration: 'none',
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700
              }} data-id="element-338" data-mp="3vkfbth9afcrs">
                  <PhoneIcon size={15} aria-hidden="true" data-id="element-339" data-mp="1fuuduvdemnpu" />
                  +57 301 653 9065
                </a>

                <div style={{
                marginTop: '36px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }} data-id="element-340" data-mp="2xqmr207ik9rh">
                  {['Respuesta en menos de 24h hábiles', 'Análisis de viabilidad del caso', 'Acompañamiento dedicado en cada etapa'].map(item => <div key={item} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }} data-id="element-341" data-mp="2trxdo7mh6s7u">
                      <span aria-hidden="true" style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: DS.color.mint,
                    border: `1px solid ${DS.color.aquaLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }} data-id="element-342" data-mp="3hi157gme31ih">
                        <CheckIcon size={13} color={DS.color.teal} strokeWidth={3} data-id="element-343" data-mp="2552zvljxddiy" />
                      </span>
                      <span style={{
                    fontFamily: DS.font.body,
                    fontSize: '14px',
                    color: DS.color.body
                  }} data-id="element-344" data-mp="20ago3h9xliyc">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Columna derecha — formulario */}
            <div style={{
            background: DS.color.white,
            border: `1px solid ${DS.color.border}`,
            borderRadius: '24px',
            padding: '36px',
            position: 'relative',
            zIndex: 1,
            boxShadow: DS.shadow.card
          }} data-id="element-345" data-mp="2e23ljkfm0xip">
              {status === 'success' ? <div className="anim-fade-in" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '40px 24px'
            }} data-id="element-346" data-mp="3abuewgqspy7s">
                  <div style={{
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                marginBottom: '24px',
                background: `linear-gradient(135deg, ${DS.color.aquaLight}, ${DS.color.aqua})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: DS.shadow.glow,
                flexShrink: 0
              }} data-id="element-347" data-mp="3t0rb4q5r70sn">
                    <CheckIcon size={32} color={DS.color.tealDeep} strokeWidth={3} data-id="element-348" data-mp="2cmrate2zx94v" />
                  </div>
                  <h3 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: '1.5rem',
                color: DS.color.ink,
                marginBottom: '12px'
              }} data-id="element-349" data-mp="2zgloctr19rts">
                    ¡Caso recibido!
                  </h3>
                  <p style={{
                fontFamily: DS.font.body,
                fontSize: '15px',
                color: DS.color.muted,
                lineHeight: 1.6,
                maxWidth: '300px',
                margin: '0 auto'
              }} data-id="element-350" data-mp="3icu0mhigr2yl">
                    Gracias, {form.name.split(' ')[0] || 'doctor(a)'}. Un asesor de Alinova te contactará muy pronto.
                  </p>
                  <div style={{
                marginTop: '28px'
              }} data-id="element-351" data-mp="9ylb5ib8msan">
                    <Btn variant="ghost" size="md" onClick={resetForm} data-id="element-352" data-mp="1ufm8pl72h2ml">
                      Enviar otro caso
                    </Btn>
                  </div>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-353" data-mp="1lewgsskxfwe5">
                  {status === 'error' && <div role="alert" style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(224,106,106,0.08)',
                border: '1px solid rgba(224,106,106,0.25)',
                fontFamily: DS.font.body,
                fontSize: '13px',
                color: '#c0504d',
                textAlign: 'center'
              }} data-id="element-354" data-mp="1gg2z0nylxj86">
                      Hubo un error al enviar. Por favor intenta de nuevo.
                    </div>}

                  <div style={{
                marginBottom: '14px'
              }} data-id="element-355" data-mp="q62hfwhuo06b">
                    <label htmlFor="cf-name" style={labelStyle} data-id="element-356" data-mp="q6m0xhn5ewmg">Nombre *</label>
                    <input id="cf-name" value={form.name} onChange={e => set('name', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Dr(a). Nombre completo" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'cf-name-error' : undefined} style={{
                  ...inputBase,
                  borderColor: errors.name ? '#e06a6a' : DS.color.border
                }} data-id="element-357" data-mp="u64ettgjy7fs" />
                    {errors.name && <span id="cf-name-error" style={errorStyle} data-id="element-358" data-mp="1rw7lji7e55c9">{errors.name}</span>}
                  </div>

                  <div className="col-2" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '14px',
                marginBottom: '14px'
              }} data-id="element-359" data-mp="1ws6ihu6sbvoa">
                    <div data-id="element-360" data-mp="2fsm2bz4fhc15">
                      <label htmlFor="cf-email" style={labelStyle} data-id="element-361" data-mp="2b01dlc0dpp4h">Correo *</label>
                      <input id="cf-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="tu@correo.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'cf-email-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.email ? '#e06a6a' : DS.color.border
                  }} data-id="element-362" data-mp="2pop619exhiq7" />
                      {errors.email && <span id="cf-email-error" style={errorStyle} data-id="element-363" data-mp="16ooh8cg76aib">{errors.email}</span>}
                    </div>
                    <div data-id="element-364" data-mp="1aps0za1d8qot">
                      <label htmlFor="cf-phone" style={labelStyle} data-id="element-365" data-mp="1fr7iwt88yh2z">Teléfono *</label>
                      <input id="cf-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Tu número de contacto" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'cf-phone-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.phone ? '#e06a6a' : DS.color.border
                  }} data-id="element-366" data-mp="j0otzmi3h28h" />
                      {errors.phone && <span id="cf-phone-error" style={errorStyle} data-id="element-367" data-mp="3nz3zp4ugueak">{errors.phone}</span>}
                    </div>
                  </div>

                  <div style={{
                marginBottom: '28px'
              }} data-id="element-368" data-mp="3eye7eb4ez61l">
                    <label htmlFor="cf-modality" style={labelStyle} data-id="element-369" data-mp="3ezz15yiwmkye">Modalidad de interés</label>
                    <select id="cf-modality" value={form.modality} onChange={e => set('modality', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} style={{
                  ...inputBase,
                  cursor: 'pointer',
                  appearance: 'none',
                  color: DS.color.ink
                }} data-id="element-370" data-mp="3a0cqnwugnita">
                      {modalities.map(m => <option key={m.value} value={m.value} style={{
                    color: DS.color.ink
                  }} data-id="element-371" data-mp="2ckjsfv84w2lf">
                          {m.label}
                        </option>)}
                    </select>
                  </div>

                  <div style={{
                marginBottom: '20px'
              }} data-id="element-372" data-mp="22pi3azduz0iu">
                    <label htmlFor="cf-consent" style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer'
                }} data-id="element-373" data-mp="27n1n0etbs5i6">
                      <input id="cf-consent" type="checkbox" required checked={consent} onChange={e => {
                    setConsent(e.target.checked);
                    if (consentError) setConsentError(undefined);
                    if (status === 'error') setStatus('idle');
                  }} aria-invalid={Boolean(consentError)} aria-describedby={consentError ? 'cf-consent-error' : undefined} style={{
                    marginTop: '3px',
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                    accentColor: DS.color.tealDeep,
                    cursor: 'pointer'
                  }} data-id="element-374" data-mp="2llrjptc8g360" />
                      <span style={{
                    fontFamily: DS.font.body,
                    fontSize: '13px',
                    color: DS.color.muted,
                    lineHeight: 1.5
                  }} data-id="element-375" data-mp="xoim3me8u39j">
                        Autorizo el tratamiento de mis datos personales de
                        acuerdo con la Política de privacidad.
                      </span>
                    </label>
                    {consentError && <span id="cf-consent-error" style={{
                  ...errorStyle,
                  display: 'block',
                  marginTop: '6px'
                }} data-id="element-376" data-mp="2t61v8dx6rrsq">
                        {consentError}
                      </span>}
                  </div>

                  <Btn variant="aqua" size="lg" fullWidth type="submit" disabled={status === 'loading'} data-id="element-377" data-mp="31zonhu26qcx6">
                    {status === 'loading' ? <><LoaderIcon size={18} className="anim-rotate" data-id="element-378" data-mp="3buq9ih15yyvj" /> Enviando...</> : 'Empezar mi primer caso'}
                  </Btn>

                  <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '9px',
                padding: '12px 20px',
                borderRadius: DS.radius.pill,
                background: '#25D366',
                color: '#fff',
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700,
                textDecoration: 'none',
                transition: `background-color 0.2s ${DS.ease.smooth}`
              }} onMouseEnter={e => e.currentTarget.style.background = '#1EBE5D'} onMouseLeave={e => e.currentTarget.style.background = '#25D366'} data-id="element-379" data-mp="3kverfmtohjdp">
                    <MessageCircleIcon size={17} aria-hidden="true" data-id="element-380" data-mp="1nxz4mwqmov28" />
                    Prefiero continuar por WhatsApp
                  </a>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const errorStyle: React.CSSProperties = {
  fontFamily: DS.font.body,
  fontSize: '11px',
  color: '#c0504d'
};